import { useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import Divider from '../common/Divider';
import {
    ResetPasswordFormInput,
    ResetPasswordFormValues,
    resetPasswordSchema
} from '../form/schema/resetPasswordSchema';

import { resetUserPassword } from '@/services/UserService';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials } from '@/state/reducers/authSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import useToast from '@/utils/hooks/useToast';
import cn from 'classnames';
import zxcvbn from 'zxcvbn';
import { Button } from '../common/Button';
import Input from '../inputs/Input';
import InputController from '../inputs/InputController';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from './Modal';

const defaultValues: Partial<ResetPasswordFormInput> = {
    current_password: undefined,
    new_password: undefined,
    confirm_new_password: undefined
};

const ResetPasswordModal = (props: ModalProps) => {
    const { opened, handlers } = props;

    const lang = useLanguage();
    const [toastError, toastSuccess] = useToast();

    const credentials = useAppSelector(selectCredentials);

    const form = useForm<ResetPasswordFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(resetPasswordSchema)
    });

    const {
        watch,
        reset,
        trigger,
        setError,
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = form;

    const onSubmit = async (data: ResetPasswordFormInput) => {
        const dataValidated = data as ResetPasswordFormValues;
        const { current_password, new_password, confirm_new_password } = dataValidated;
        if (credentials) {
            if (new_password === confirm_new_password) {
                const resp = await resetUserPassword(credentials, current_password, new_password);

                if (resp.succeeded) {
                    const title = resp.message[0];
                    const body = 'Your password had been successfully changed to the new password.';
                    toastSuccess({ body, title });
                    handlers.close();
                } else {
                    const title = 'Change User Password';
                    const body = resp.message[0];
                    toastError({ body, title, persist: true });
                }
            } else {
                const title = 'Change User Password';
                const body = 'Confirm Password does not match with the New Password.';
                setError('confirm_new_password', { type: 'custom', message: body });
                toastError({ body, title, persist: true });
            }
        }
    };

    const [new_password, confirm_new_password] = watch(['new_password', 'confirm_new_password']);

    useEffect(() => {
        if (confirm_new_password) trigger('confirm_new_password');
    }, [new_password, confirm_new_password]);

    const passwordStrength = useMemo(() => {
        if (!new_password) return 0;
        return zxcvbn(new_password).score;
    }, [new_password]);

    const strengthInfo = useMemo(() => {
        if (passwordStrength === 0)
            return {
                label: '',
                color: 'bg-transparent',
                text: 'text-brand-neutral-400'
            };
        if (passwordStrength <= 1)
            return {
                label: 'Weak',
                color: 'bg-red-500',
                text: 'text-red-500'
            };
        if (passwordStrength === 2)
            return {
                label: 'Medium',
                color: 'bg-yellow-500',
                text: 'text-yellow-600'
            };
        if (passwordStrength === 3)
            return {
                label: 'Good',
                color: 'bg-brand-green-light',
                text: 'text-brand-green-light'
            };
        return {
            label: 'Strong',
            color: 'bg-brand-green',
            text: 'text-brand-green'
        };
    }, [passwordStrength]);

    const onError = (e: FieldErrors) => console.log(e);

    useEffect(() => {
        if (opened) {
            reset(defaultValues);
        }
    }, [opened]);

    return (
        <Modal {...props} className="h-auto" size="max-w-md">
            <ModalHeader>
                <ModalTitle>{lang.settings.changePassword}</ModalTitle>
                <ModalClose handlers={handlers} />
            </ModalHeader>
            <Divider />
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(onSubmit, onError)}>
                    <div className="p-2 sm:p-4">
                        <div className="flex flex-col gap-4">
                            <div className="space-y-3 sm:space-y-2">
                                <InputController
                                    name="current_password"
                                    label="Current Password"
                                    placeholder="••••••••"
                                    required>
                                    <Input type="password" />
                                </InputController>
                                <div className={cn('', { 'space-y-3 sm:space-y-2': new_password })}>
                                    <InputController
                                        name="new_password"
                                        label="New Password"
                                        placeholder="••••••••"
                                        required>
                                        <Input type="password" />
                                    </InputController>
                                    <div
                                        className={`space-y-3 transition-all duration-300 ease-in-out overflow-hidden ${
                                            new_password ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
                                        }`}
                                        id="password-strength">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-xs text-brand-neutral-300">Password Strength</span>
                                            <span
                                                className={`text-xs font-bold transition-colors duration-300 ${strengthInfo.text}`}>
                                                {strengthInfo.label}
                                            </span>
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="h-2 w-full bg-brand-neutral-600 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full transition-all duration-500 ease-out ${strengthInfo.color}`}
                                                style={{
                                                    width: `${passwordStrength * 25}%`
                                                }}
                                                role="progressbar"
                                                aria-valuenow={passwordStrength * 25}
                                                aria-valuemin={0}
                                                aria-valuemax={100}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <InputController
                                    name="confirm_new_password"
                                    label="Confirm Password"
                                    placeholder="••••••••"
                                    required>
                                    <Input type="password" />
                                </InputController>
                            </div>
                        </div>
                    </div>
                    <Divider />
                    <div className="flex flex-row justify-between text-brand-text text-sm items-center p-2 sm:p-4">
                        <div className="justify-end flex space-x-2">
                            <button
                                type="button"
                                className="rounded-md p-2 px-4 bg-brand-neutral-600 hover:bg-brand-neutral-500"
                                onClick={handlers.close}>
                                {lang.common.cancel}
                            </button>
                            <Button
                                type="button"
                                className="rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-light"
                                onClick={handleSubmit(onSubmit, onError)}
                                isLoading={isSubmitting}
                                disabled={isSubmitting || !(passwordStrength >= 3) || !isValid}>
                                {lang.settings.changePassword}
                            </Button>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </Modal>
    );
};

export default ResetPasswordModal;
